import { isAxiosError } from "axios";
import notify from "../lib/notify";
import { t } from "i18next";
import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useState,
} from "react";
import { getUser, getUserData, signIn as sendSignInRequest } from "../api/auth";
import axios from "../lib/axios";
import type { AuthContextType, Regulation, User } from "../types";
import { useCompanyContext } from "./company";

const AuthContext = createContext<AuthContextType>({
  loading: false,
} as AuthContextType);

function AuthProvider(props: React.PropsWithChildren) {
  const companyContext = useCompanyContext();

  const [user, setUser] = useState<User>();
  const [loading, setLoading] = useState(true);

  const refreshUser = useCallback(async () => {
    const result = await getUser();
    if (result.isOk) {
      setUser(result.data);
      companyContext.setCompanyId(result.data?.companyId ?? 0);
    }
  }, []);

  useEffect(() => {
    (async function () {
      const result = await getUser();
      if (result.isOk) {
        setUser(result.data);
        companyContext.setCompanyId(result.data?.companyId ?? 0);
      }
      setLoading(false);
    })();
  }, []);

  const signIn = useCallback(
    async (
      email: string,
      password: string,
      companyId: number,
      rememberMe: boolean,
    ): Promise<{
      isOk: boolean;
      data?: User;
      message?: string;
      requiresTwoFactorAuthentication?: boolean;
      isLockedOut?: boolean;
      confirmationRequired?: boolean;
      peselLoginBlock?: boolean;
      firstChangePassword?: boolean;
      companyId?: number;
      enable2Fa?: boolean;
    }> => {
      const result = await sendSignInRequest(
        email,
        password,
        companyId,
        rememberMe,
      );

      if (result.isOk) {
        const requiresTwoFactorAuthentication =
          result.data?.requiresTwoFactorAuthentication;
        const isLockedOut = result.data?.isLockedOut;
        const peselLoginBlock = result.data?.peselLoginBlock;
        const confirmationRequired = result.data?.confirmationRequired;
        const firstChangePassword = result.data?.firstChangePassword;
        const enable2Fa = result.data?.enable2Fa;
        const companyId = result.data?.companyId;

        if (requiresTwoFactorAuthentication) {
          return { isOk: true, requiresTwoFactorAuthentication };
        } else if (enable2Fa) {
          return { isOk: true, enable2Fa };
        } else if (isLockedOut) {
          return { isOk: true, isLockedOut };
        } else if (confirmationRequired) {
          companyContext.setCompanyId(companyId);
          return { isOk: true, confirmationRequired };
        } else if (peselLoginBlock) {
          return { isOk: true, peselLoginBlock };
        } else if (firstChangePassword) {
          return { isOk: true, firstChangePassword };
        } else {
          const resultData = await getUserData();
          setUser(resultData.data);
          companyContext.setCompanyId(resultData.data?.companyId ?? 0);

          return {
            isOk: true,
            data: resultData.data,
          };
        }
      } else {
        return {
          isOk: false,
          message: result.error || t("common.unknownErrorOccurred"),
        };
      }
    },

    [companyContext],
  );

  const signInAuth = useCallback(async () => {
    try {
      const resultData = await getUserData();
      setUser(resultData.data);
      companyContext.setCompanyId(resultData.data?.companyId ?? 0);

      return { isOk: true, data: resultData.data };
    } catch (error) {
      return {
        isOk: false,
        error: "An error occurred while fetching user data.",
      };
    }
  }, []);

  const signOut = useCallback(() => {
    axios
      .post("/api/auth/logout")
      .then(() => setUser(undefined))
      .catch((error) => {
        let message = t("common.unknownErrorOccurred");

        if (isAxiosError(error)) {
          if (error.response) {
            if (error.response.data && "message" in error.response.data) {
              message = error.response.data.message;
            }
          } else if (error.request) {
            message = t("common.noResponseFromServer");
          }
        }

        notify(message, "error", 5000);
      })
      .finally(() => setUser(undefined));
  }, []);

  const setRegulationAccepted = useCallback((regulation: Regulation) => {
    if (regulation === "TermsOfUse") {
      setUser((user) => {
        if (!user) {
          return undefined;
        }

        return {
          ...user,
          termsOfUseAccepted: true,
        };
      });
    }

    if (regulation === "GeneralDataProtectionRegulation") {
      setUser((user) => {
        if (!user) {
          return undefined;
        }

        return {
          ...user,
          generalDataProtectionRegulationAccepted: true,
        };
      });
    }

    if (regulation === "ProcessingOfEmailAddress") {
      setUser((user) => {
        if (!user) {
          return undefined;
        }

        return {
          ...user,
          processingOfEmailAddressAccepted: true,
        };
      });
    }
  }, []);

  return (
    <AuthContext.Provider
      value={{
        user,
        signIn,
        signOut,
        signInAuth,
        loading,
        refreshUser,
        setRegulationAccepted,
      }}
      {...props}
    />
  );
}

const useAuth = () => useContext(AuthContext);

export { AuthProvider, useAuth };
